import type { PlanAddOn, CategoryType } from '@t/productOffering.types';
import type { ParsedAddOn, AddOnType } from '@t/addOn.types';

const parseRawDescription = (raw?: string): Record<string, unknown> | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Determines the add-on type based on the plan category and add-on data.
 * TV plans have theme packages and individual channels; Internet/Phone have service add-ons.
 * Theme packages have a `channelList` array in their description JSON; individual channels do not.
 */
const classifyAddOn = (addOn: PlanAddOn, planCategory: CategoryType): AddOnType => {
  if (planCategory !== 'Video') return 'serviceAddOn';
  const desc = parseRawDescription(addOn.rawDescription);
  const channelList = desc?.channelList;
  return Array.isArray(channelList) && channelList.length > 0 ? 'themePackage' : 'individualChannel';
};

export const parsePlanAddOn = (addOn: PlanAddOn, planCategory: CategoryType): ParsedAddOn => {
  const type = classifyAddOn(addOn, planCategory);
  return {
    code: addOn.code,
    name: addOn.name,
    price: addOn.price,
    description: addOn.description,
    type,
    categoryId: addOn.category?.id,
    categoryName: addOn.category?.name,
    imageUrl: addOn.imageUrl,
    descriptionImage: addOn.descriptionImage,
    channels: addOn.channels,
    discountTag: addOn.discountTag,
    discountMessage: addOn.discountMessage,
    supportsQuantity: addOn.supportsQuantity ?? false,
  };
};

export const selectParsedAddOns = (addOns: PlanAddOn[], planCategory: CategoryType): ParsedAddOn[] =>
  addOns.map((addOn) => parsePlanAddOn(addOn, planCategory));
